// src/components/dashboard/FinancialDetailsTable.jsx
import React, { useMemo, useState } from "react";

function formatEuro(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "0 €";
  return n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}

const FinancialDetailsTable = ({ year, totalPrevisionnel = 0, rows = [] }) => {
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("desc"); // desc | asc

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    let arr = [...(rows || [])];

    if (term) {
      arr = arr.filter((r) =>
        String(r.company_name || "")
          .toLowerCase()
          .includes(term)
      );
    }

    arr.sort((a, b) => {
      const va = Number(a.taxes_previsionnel) || 0;
      const vb = Number(b.taxes_previsionnel) || 0;
      return sortDir === "desc" ? vb - va : va - vb;
    });

    return arr;
  }, [rows, search, sortDir]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold text-gray-900">
            Détails financiers — Année {year}
          </div>
          <div className="text-[10px] text-gray-500">
            Prévisionnel taxes (CSPE) calculé depuis les résumés ERA.
          </div>
        </div>

        <div className="text-right">
          <div className="text-[10px] text-gray-500">Total prévisionnel</div>
          <div className="text-sm font-bold text-amber-700">
            {formatEuro(totalPrevisionnel)}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="px-3 py-2 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un client…"
          className="w-full sm:w-80 text-xs px-2 py-2 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-teal-100"
        />

        <button
          type="button"
          onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
          className="text-xs px-2 py-2 border border-gray-200 rounded hover:bg-gray-50"
          title="Trier par montant"
        >
          Trier {sortDir === "desc" ? "↓" : "↑"}
        </button>

        <div className="ml-auto text-[10px] text-gray-500">
          {filtered.length} client{filtered.length > 1 ? "s" : ""}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="text-left">
              <th className="px-3 py-2 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                Client
              </th>
              <th className="px-3 py-2 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                Année
              </th>
              <th className="px-3 py-2 text-[10px] font-semibold text-gray-600 uppercase tracking-wider text-right">
                Prévisionnel taxes
              </th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-100">
            {filtered.length > 0 ? (
              filtered.map((r) => (
                <tr key={r.account_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-xs text-gray-900 font-medium">
                    {r.company_name || "Client"}
                    <div className="text-[10px] text-gray-400 font-mono">
                      {r.account_id}
                    </div>
                  </td>

                  <td className="px-3 py-2 text-xs text-gray-700">
                    {r.annee_fiscale ?? year}
                  </td>

                  <td className="px-3 py-2 text-xs text-right font-bold text-amber-700">
                    {formatEuro(r.taxes_previsionnel || 0)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="px-3 py-10 text-center text-xs text-gray-600">
                  Aucun résultat.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinancialDetailsTable;
